import { useState } from 'react';
import { Columns3 } from 'lucide-react';
import type { ColumnOverride, ManualPickerRequired } from '../lib/ami/types';
import { isKWhHeader } from '../lib/fileParser';
import { columnLetter, formatSample } from '../lib/ami/pickerFormat';

/**
 * The person's hand on the column choice.
 *
 * Opened for one of two reasons. Detection gave up — no column could be read
 * as a date/time, or none as demand — which is rare, because detection reads a
 * file four different ways first, and never happens on a clean parse. Or the
 * parse succeeded and the person believes it read the wrong column; then the
 * panel opens with the parse's own choices preselected so only the wrong one
 * needs changing. Either way it shows the file as TrueLoad sees it — every
 * column with its first values — and the parse runs again with the chosen
 * columns forced. Every safety check still applies: a column that yields no
 * readings, or readings outside a plausible window, is refused exactly as an
 * automatic pick would be. The choice is disclosed on the report as columns
 * chosen by the preparer, and a correction never counts as a data replacement.
 */

export type ColumnPickerReason =
  | { kind: 'missing'; role: 'timestamp' | 'demand' }
  | { kind: 'repick'; current: ColumnOverride };

interface ColumnPickerProps {
  fileName: string;
  choices: Pick<ManualPickerRequired, 'columns' | 'candidates'>;
  reason: ColumnPickerReason;
  /** Position in a multi-file batch, for "File 2 of 3". */
  position?: { index: number; total: number };
  busy?: boolean;
  onConfirm: (override: ColumnOverride) => void;
  onCancel: () => void;
}

interface Candidate { index: number; header: string | null; samples: unknown[] }

function labelFor(column: { index: number; header: string | null }): string {
  return `${columnLetter(column.index)}: ${column.header?.trim() || `column ${column.index + 1}`}`;
}

function optionLabel(column: Candidate): string {
  const examples = column.samples.map(formatSample).filter(Boolean).slice(0, 2).join(', ');
  return examples ? `${labelFor(column)}  (e.g. ${examples})` : labelFor(column);
}

export function ColumnPicker({ fileName, choices, reason, position, busy = false, onConfirm, onCancel }: ColumnPickerProps) {
  const timestampCandidates = choices.candidates.timestamp;
  const current = reason.kind === 'repick' ? reason.current : null;
  // A correction starts from what the parse chose, so only the wrong column
  // needs touching. On a refusal, one candidate needs no decision; it is
  // preselected and the person confirms it by choosing the demand column.
  const [timestampIndex, setTimestampIndex] = useState<number | null>(
    current ? current.timestampIndex : timestampCandidates.length === 1 ? timestampCandidates[0].index : null
  );
  const [demandIndex, setDemandIndex] = useState<number | null>(current ? current.demandIndex : null);
  // Only for files that keep the time of day in its own column (ComEd, Opower).
  const [timeIndex, setTimeIndex] = useState<number | null>(current?.timeIndex ?? null);

  const demandCandidates = choices.candidates.demand.filter(c => c.index !== timestampIndex);
  const changed = !current || timestampIndex !== current.timestampIndex || demandIndex !== current.demandIndex ||
    timeIndex !== (current.timeIndex ?? null);
  const ready = timestampIndex !== null && demandIndex !== null && timestampIndex !== demandIndex && changed && !busy;

  const columnLabel = (index: number) => {
    const column = choices.columns.find(c => c.index === index);
    return column ? labelFor(column) : `column ${index + 1}`;
  };
  const demandHeader = choices.columns.find(c => c.index === demandIndex)?.header ?? null;
  const unitNote = demandIndex === null
    ? null
    : isKWhHeader(demandHeader)
      ? 'This column will be read as kWh and converted to kW using the interval between readings.'
      : /kva/i.test(demandHeader ?? '')
        ? 'This column will be read as kVA, with the kVA-to-kW note on the report.'
        : 'This column will be read as kW.';

  const foundTimestamp = reason.kind === 'missing' && reason.role === 'demand' && timestampCandidates.length === 1
    ? labelFor(timestampCandidates[0])
    : null;
  const explanation = current
    ? `TrueLoad read the date and time from ${columnLabel(current.timestampIndex)} and the demand from ` +
      `${columnLabel(current.demandIndex)}. If that is not right, choose the correct columns and TrueLoad will ` +
      'read the file again. This does not count as a data replacement.'
    : (foundTimestamp
        ? `TrueLoad found the date and time in ${foundTimestamp} but could not tell which column holds the demand readings. Choose it below.`
        : 'TrueLoad could not tell which column holds the date and time. Choose it, then choose the column with the demand readings.') +
      ' TrueLoad will read the file again with your choice, and every check still applies.';
  const sampleRowCount = Math.min(5, choices.columns.reduce((m, c) => Math.max(m, c.samples.length), 0));

  const cellClass = (index: number) =>
    index === timestampIndex ? 'bg-blue-100' : index === timeIndex ? 'bg-amber-100' : index === demandIndex ? 'bg-green-100' : '';
  const timeCandidates = choices.columns.filter(c => c.index !== timestampIndex && c.index !== demandIndex);

  return (
    <div data-testid="ami-column-picker" className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
      <div className="flex items-start gap-2">
        <Columns3 className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-900">
            {current ? 'Choose the columns yourself' : 'Help TrueLoad read this file'}
            {position && position.total > 1 && (
              <span className="font-normal text-blue-700"> (file {position.index} of {position.total})</span>
            )}
          </p>
          <p className="text-xs text-blue-800 mt-0.5 break-all">{fileName}</p>
          <p className="text-sm text-blue-900 mt-2">{explanation}</p>

          {/* minmax(0,1fr) tracks on both breakpoints: an auto track would grow to the
              longest option label and push the panel past a phone screen. */}
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">
              Date and time column
              <select
                data-testid="ami-column-picker-timestamp"
                value={timestampIndex ?? ''}
                disabled={busy}
                onChange={e => {
                  const next = e.target.value === '' ? null : Number(e.target.value);
                  setTimestampIndex(next);
                  if (next !== null && next === demandIndex) setDemandIndex(null);
                  if (next !== null && next === timeIndex) setTimeIndex(null);
                }}
                className="mt-1 block w-full max-w-full min-w-0 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal text-gray-900"
              >
                <option value="">Choose a column…</option>
                {timestampCandidates.map(c => (
                  <option key={c.index} value={c.index}>{optionLabel(c)}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Demand column
              <select
                data-testid="ami-column-picker-demand"
                value={demandIndex ?? ''}
                disabled={busy}
                onChange={e => {
                  const next = e.target.value === '' ? null : Number(e.target.value);
                  setDemandIndex(next);
                  if (next !== null && next === timeIndex) setTimeIndex(null);
                }}
                className="mt-1 block w-full max-w-full min-w-0 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal text-gray-900"
              >
                <option value="">Choose a column…</option>
                {demandCandidates.map(c => (
                  <option key={c.index} value={c.index}>{optionLabel(c)}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-gray-700 sm:col-span-2">
              Time of day column <span className="font-normal text-gray-500">(only if the time is in its own column)</span>
              <select
                data-testid="ami-column-picker-time"
                value={timeIndex ?? ''}
                disabled={busy}
                onChange={e => setTimeIndex(e.target.value === '' ? null : Number(e.target.value))}
                className="mt-1 block w-full max-w-full min-w-0 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm font-normal text-gray-900"
              >
                <option value="">None: the date and time are together</option>
                {timeCandidates.map(c => (
                  <option key={c.index} value={c.index}>{optionLabel(c)}</option>
                ))}
              </select>
            </label>
          </div>
          {unitNote && <p className="mt-2 text-xs text-blue-800">{unitNote}</p>}

          <p className="mt-3 text-xs font-medium text-gray-700">The file as TrueLoad sees it (first {sampleRowCount} rows)</p>
          <div className="mt-1 overflow-x-auto rounded border border-blue-200 bg-white">
            <table className="text-xs whitespace-nowrap">
              <thead>
                <tr>
                  {choices.columns.map(c => (
                    <th key={c.index} className={`px-2 py-1 text-left font-medium text-gray-700 border-b border-blue-200 ${cellClass(c.index)}`}>
                      <span className="text-gray-400">{columnLetter(c.index)}</span>
                      <span className="ml-1">{c.header?.trim() || `column ${c.index + 1}`}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: sampleRowCount }, (_, r) => (
                  <tr key={r}>
                    {choices.columns.map(c => (
                      <td key={c.index} className={`px-2 py-1 text-gray-800 ${cellClass(c.index)}`}>{formatSample(c.samples[r])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="ami-column-picker-confirm"
              disabled={!ready}
              onClick={() => {
                if (timestampIndex === null || demandIndex === null) return;
                onConfirm({ timestampIndex, demandIndex, timeIndex });
              }}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Reading the file…' : current ? 'Read the file again with these columns' : 'Read the file with these columns'}
            </button>
            <button
              type="button"
              data-testid="ami-column-picker-cancel"
              disabled={busy}
              onClick={onCancel}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {current ? 'Keep the current columns' : 'Not now'}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            Your choice is recorded on the report as columns chosen by the preparer.
          </p>
        </div>
      </div>
    </div>
  );
}
